import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, of, Subject, take } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import XRegExp from 'xregexp';
import { NgxSpinnerService } from 'ngx-spinner';
import { InlineSpinnerComponent } from '@jump-tech-frontend/angular-common';
import { JuiButton, JuiSkeletonLoader } from '@jump-tech-frontend/core-components-angular';
import { AsyncPipe } from '@angular/common';
import { ClassificationResult } from '@jump-tech-frontend/domain';
import { ImageAnalysisRecordService, parseStoredAsset } from '../analysis/image-analysis-record.service';

@Component({
  selector: 'crds-http-image',
  template: ` <div class="http-image-wrapper">
    @if (!!latestValue) {
      <img
        [draggable]="isDraggable"
        (click)="clicked.emit(true)"
        [id]="id"
        [class]="imageClass + ' http-image-wrapper__image'"
        [src]="latestValue"
        [alt]="alt"
        [attr.data-qa]="qaHook"
        (load)="loaded$.next(true)"
        (mouseenter)="hoveringImage$.next(true)"
        (mouseleave)="leaveImageHover()"
      />
    }
    @if (showDownload$ | async) {
      <jui-button
        color="primary"
        class="http-image-wrapper__download"
        size="xs"
        display="contained"
        [loading]="downloadingImage$ | async"
        (click)="onImageDownloadEvent()"
        [attr.data-qa]="'downloadImageButton'"
        (mouseenter)="hoveringDownload$.next(true)"
        (mouseleave)="hoveringDownload$.next(false)"
      >
        <span slot="icon" class="material-icons">download</span>
      </jui-button>
    }
    @if (!latestValue) {
      @if (!skeletonLoaderHeight) {
        <crds-inline-spinner [name]="uuid" qaHook="inlineSpinner" type="ball-clip-rotate"></crds-inline-spinner>
      }
      <jui-skeleton-loader
        height="{{ skeletonLoaderHeight }}px"
        width="100%"
        [attr.data-qa]="'skeletonLoader'"
      ></jui-skeleton-loader>
    }
  </div>`,
  styleUrls: ['./http-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [JuiButton, JuiSkeletonLoader, InlineSpinnerComponent, AsyncPipe]
})
export class HttpImageComponent implements OnInit, OnChanges, OnDestroy {
  private httpClient = inject(HttpClient);
  private domSanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private spinnerService = inject(NgxSpinnerService);
  private imageAnalysisRecordService = inject(ImageAnalysisRecordService);

  private readonly _uuid: string;
  private _id: string;
  private _src: string | SafeUrl | null;
  private _alt: string;
  private _imageClass: string;
  private _qaHook: string;
  private _latestValue: string | SafeUrl | null = null;
  private transformValue = new BehaviorSubject<string | SafeUrl | null>(null);

  private _unsubscribe$ = new Subject();
  private _errorSrc: string;
  private _useHttp: boolean;

  private apiPattern = /https:\/\/api\..*\.?jumptech.co.uk\/store/;

  downloadingImage$ = new BehaviorSubject<boolean>(false);
  loaded$ = new BehaviorSubject<boolean>(false);
  hoveringImage$ = new BehaviorSubject<boolean>(false);
  hoveringDownload$ = new BehaviorSubject<boolean>(false);
  showDownload$ = new BehaviorSubject<boolean>(false);

  private currentBlobUrl: string | null = null;

  get id(): string {
    return this._id || this.uuid;
  }

  @Input() set id(value: string) {
    this._id = value;
  }

  get src(): string | SafeUrl | null {
    return this._src;
  }

  @Input() set src(value: string | SafeUrl | null) {
    this._src = this.domSanitizer.sanitize(4, value);
  }

  get imageClass(): string {
    return this._imageClass;
  }

  @Input() set imageClass(value: string) {
    this._imageClass = value;
  }

  get alt(): string {
    return this._alt || '';
  }

  @Input() set alt(value: string) {
    this._alt = value;
  }

  get qaHook(): string {
    return this._qaHook;
  }

  @Input() set qaHook(value: string) {
    this._qaHook = value;
  }

  get latestValue(): string | SafeUrl | null {
    return this._latestValue;
  }

  set latestValue(value: string | SafeUrl | null) {
    this._latestValue = value;
  }

  get errorSrc(): string {
    return this._errorSrc;
  }

  @Input() set errorSrc(value: string) {
    this._errorSrc = value;
  }

  get uuid() {
    return this._uuid;
  }

  @Input() downloadUrl: string;
  @Input() skeletonLoaderHeight = 0;
  @Input() allowDownload = true;
  @Input() fileName: string | null = null;
  @Input() isDraggable = true;
  @Input() fetchMetadata = false; // Enable metadata fetching for S3 images

  @Output() clicked = new EventEmitter<boolean>();
  @Output() metadataLoaded = new EventEmitter<ClassificationResult | null>();

  constructor() {
    this._uuid = this.getUniqueId(4);
    this.setUpSubscription();
  }

  ngOnInit(): void {
    this.transform(this.src);
    if (this.allowDownload) {
      combineLatest([this.loaded$, this.hoveringImage$, this.hoveringDownload$])
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe(([loaded, hoveringImage, hoveringDownload]) => {
          this.showDownload$.next(!!this.downloadUrl && loaded && (hoveringImage || hoveringDownload));
        });
    }
  }

  leaveImageHover() {
    this.hoveringDownload$.pipe(debounceTime(10), take(1)).subscribe(hoveringDownload => {
      if (!hoveringDownload) {
        this.hoveringImage$.next(false);
      }
    });
  }

  ngOnChanges(): void {
    this.transform(this.src);
  }

  ngOnDestroy() {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }

    this._unsubscribe$.next(null);
    this._unsubscribe$.complete();
  }

  @HostListener('contextmenu', ['$event'])
  onRightClick(event: Event) {
    event.preventDefault();
  }

  private transform(imagePath: string | SafeUrl | null) {
    if (!imagePath) {
      return;
    }

    if (this._useHttp === false || !XRegExp(this.apiPattern).test(imagePath.toString())) {
      this.latestValue = imagePath;
      return;
    }

    this.transformValue.next(imagePath.toString());
  }

  private setUpSubscription(): void {
    const thatSpinnerService = this.spinnerService;
    this.transformValue
      .asObservable()
      .pipe(
        takeUntil(this._unsubscribe$),
        filter((v): v is string => !!v),
        distinctUntilChanged(),
        switchMap((imagePath: string) => {
          thatSpinnerService.show(this.uuid).then();
          // add skip header to prevent pathway error interceptor from handling errors
          return this.httpClient
            .get(imagePath, {
              headers: { skip: 'true', accept: 'image/png' },
              responseType: 'blob'
            })
            .pipe(
              map((image: Blob | null) => {
                thatSpinnerService.hide(this.uuid).then();
                if (!image) {
                  return '';
                }
                this.resolveAnalysis(imagePath);

                if (this.currentBlobUrl) {
                  URL.revokeObjectURL(this.currentBlobUrl);
                }

                const blobUrl = URL.createObjectURL(image);
                this.currentBlobUrl = blobUrl;
                return blobUrl;
              }),
              map((unsafeBlobUrl: string) => this.domSanitizer.bypassSecurityTrustUrl(unsafeBlobUrl)),
              filter(blobUrl => blobUrl !== this.latestValue),
              // if the request errors out we return the error image's path value
              catchError(() => {
                thatSpinnerService.hide(this.uuid).then();
                return of(this.errorSrc || '');
              })
            );
        }),
        tap((imagePath: string | SafeUrl) => {
          this.latestValue = imagePath;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private resolveAnalysis(imagePath: string): void {
    if (!this.fetchMetadata) {
      return;
    }

    const asset = parseStoredAsset(imagePath);
    if (!asset) {
      this.metadataLoaded.emit(null);
      return;
    }

    this.imageAnalysisRecordService
      .getAnalysisForAsset(asset)
      .pipe(take(1), takeUntil(this._unsubscribe$))
      .subscribe({
        next: analysis => this.metadataLoaded.emit(analysis),
        error: () => this.metadataLoaded.emit(null)
      });
  }

  getUniqueId(parts: number): string {
    const stringArr = [];
    for (let i = 0; i < parts; i++) {
      const S4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
      stringArr.push(S4);
    }
    return stringArr.join('-');
  }

  async onImageDownloadEvent() {
    this.downloadingImage$.next(true);
    try {
      const downloadHelper = document.createElement('a');
      if (this.downloadUrl.indexOf('base64,') !== -1) {
        this.downloadBase64Image(this.downloadUrl);
      } else {
        const response: HttpResponse<Blob> = await firstValueFrom(
          this.httpClient.get(this.downloadUrl + (this.downloadUrl.endsWith('&') ? 'size=o' : '&size=o'), {
            headers: { skip: 'true', accept: 'image/png' },
            observe: 'response',
            responseType: 'blob'
          })
        );
        this.downloadImage(response, downloadHelper);
      }
    } finally {
      this.downloadingImage$.next(false);
    }
  }

  downloadBase64Image(encodedImage: string): void {
    const a = document.createElement('a');
    const fileExt = encodedImage.split(';')[0].split('/')[1] ?? 'jpeg';
    a.href = encodedImage;
    a.download = this.fileName ? `${this.fileName}.${fileExt}` : 'temp-image.jpeg';
    a.click();
  }

  downloadImage(response: HttpResponse<Blob>, downloadHelper: HTMLAnchorElement) {
    if (response?.body) {
      const blobUrl = URL.createObjectURL(response.body);
      downloadHelper.href = blobUrl;
      const fileName = this.getFileNameFromDownloadUrl();
      downloadHelper.setAttribute('download', fileName);
      downloadHelper.setAttribute('target', '_blank');
      document.body.appendChild(downloadHelper);
      downloadHelper.click();

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        document.body.removeChild(downloadHelper);
      }, 100);
    }
  }

  private getFileNameFromDownloadUrl() {
    const downloadAttrR = new RegExp('%2F(.+)\\?signature');
    const downloadAttrMatch: RegExpMatchArray | null = this.downloadUrl.match(downloadAttrR);
    const fullFileName = downloadAttrMatch?.[1] ?? 'download';
    return fullFileName.replace(/_[0-9]{2,}/, '');
  }
}
